import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { headerNav } from '../data/mock';
import { Menu, X, Phone, Mail, Truck } from 'lucide-react';

const Header = ({ onCtaClick, onCallClick }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-sm shadow-sm border-b border-slate-100'
          : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <a href="/" className="flex items-center space-x-2 group">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center transition-colors ${
              isScrolled 
                ? 'bg-slate-800 group-hover:bg-slate-700' 
                : 'bg-white/20 backdrop-blur-sm group-hover:bg-white/30 border border-white/30'
            }`}>
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className={`font-semibold text-xl tracking-tight transition-colors ${
              isScrolled ? 'text-slate-800' : 'text-white drop-shadow-md'
            }`}>
              pakuskapis.lv<sup className="text-xs">™</sup>
            </span>
          </a>

          {/* Desktop Navigation & CTA */}
          <div className="hidden md:flex items-center space-x-3">
            <Link
              to="/pasts-un-logistika"
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                location.pathname === '/pasts-un-logistika'
                  ? isScrolled
                    ? 'bg-slate-100 text-slate-800'
                    : 'bg-white/20 text-white'
                  : isScrolled
                    ? 'text-slate-600 hover:text-slate-800 hover:bg-slate-100'
                    : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              <span className="flex items-center">
                <Truck className="w-4 h-4 mr-2" />
                Pasts & loģistika
              </span>
            </Link>
            <Button
              variant="ghost"
              onClick={onCallClick}
              className={`transition-colors ${
                isScrolled 
                  ? 'text-slate-600 hover:text-slate-800 hover:bg-slate-100' 
                  : 'text-white/90 hover:text-white hover:bg-white/10'
              }`}
            >
              <Phone className="w-4 h-4 mr-2" />
              {headerNav.ctaSecondary}
            </Button>
            <Button
              onClick={onCtaClick}
              className={`px-6 transition-all ${
                isScrolled 
                  ? 'bg-slate-800 hover:bg-slate-700 text-white' 
                  : 'bg-white hover:bg-slate-100 text-slate-900 shadow-lg'
              }`}
            >
              <Mail className="w-4 h-4 mr-2" />
              {headerNav.ctaPrimary}
            </Button>
          </div>

          {/* Mobile menu button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              isScrolled 
                ? 'hover:bg-slate-100' 
                : 'hover:bg-white/10'
            }`}
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            aria-label={isMobileMenuOpen ? 'Aizvērt izvēlni' : 'Atvērt izvēlni'}
          >
            {isMobileMenuOpen ? (
              <X className={`w-6 h-6 ${isScrolled ? 'text-slate-800' : 'text-white'}`} />
            ) : (
              <Menu className={`w-6 h-6 ${isScrolled ? 'text-slate-800' : 'text-white'}`} />
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className={`md:hidden py-4 border-t ${
            isScrolled 
              ? 'border-slate-100 bg-white' 
              : 'border-white/20 bg-slate-900/95 backdrop-blur-sm'
          }`}>
            <div className="flex flex-col space-y-3">
              <Link
                to="/pasts-un-logistika"
                onClick={() => setIsMobileMenuOpen(false)}
                className={`flex items-center px-4 py-2 rounded-lg font-medium transition-colors ${
                  location.pathname === '/pasts-un-logistika'
                    ? isScrolled
                      ? 'bg-slate-100 text-slate-800'
                      : 'bg-white/20 text-white'
                    : isScrolled
                      ? 'text-slate-600 hover:bg-slate-100'
                      : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                <Truck className="w-4 h-4 mr-2" />
                Pasts & loģistika
              </Link>
              <Button
                variant="ghost"
                onClick={() => {
                  onCallClick();
                  setIsMobileMenuOpen(false);
                }}
                className={`justify-start ${
                  isScrolled ? 'text-slate-600' : 'text-white/90 hover:text-white hover:bg-white/10'
                }`}
              >
                <Phone className="w-4 h-4 mr-2" />
                {headerNav.ctaSecondary}
              </Button>
              <Button
                onClick={() => {
                  onCtaClick();
                  setIsMobileMenuOpen(false);
                }}
                className={`${
                  isScrolled 
                    ? 'bg-slate-800 hover:bg-slate-700 text-white' 
                    : 'bg-white hover:bg-slate-100 text-slate-900'
                }`}
              >
                <Mail className="w-4 h-4 mr-2" />
                {headerNav.ctaPrimary}
              </Button>
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Header;
